import { useEffect, useState } from 'react';
import { useGame } from './net/useGame.js';
import { watchLobby } from './net/online.js';
import { loadLocal } from './net/localStore.js';
import { applyUpdate, onUpdateReady } from './pwa.js';
import { Table } from './ui/Table.js';
import { Login } from './ui/Login.js';
import { Lobby, type TableStatus } from './ui/Lobby.js';
import { Countdown } from './ui/Countdown.js';
import { Logo } from './ui/Logo.js';

const NAME_KEY = 'ubs:name';

const readName = (): string => {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return ''; // gizli sekmede depolama kapalı olabilir
  }
};

/**
 * Dikey tutulan telefonda "yan çevir" uyarısı.
 *
 * Manifest'teki `orientation: landscape` Android'de kurulu uygulamada
 * çalışıyor ama iOS onu yok sayıyor ve tarayıcıda hiçbir platformda
 * uygulanmıyor. Görünürlüğü tamamen CSS'te: yalnızca DOKUNMATİK cihaz +
 * dikey yönelimde açılıyor, masaüstünde dar pencerede açılmıyor.
 */
function RotateHint() {
  return (
    <div className="rotate-hint" role="alert">
      <Logo />
      <p>Telefonu yan çevir</p>
    </div>
  );
}

export function App() {
  const game = useGame();
  /**
   * Giriş HER AÇILIŞTA soruluyor.
   *
   * Ad diskte kalıyor (alana hazır geliyor) ama soru her seferinde soruluyor:
   * kapı ancak her girişte sorulursa kapı. Adı saklayıp girişi atlamak
   * denendi ve bırakıldı — bir kez giren cihaz sonsuza kadar içeride kalıyordu.
   */
  const [name, setName] = useState('');
  /**
   * Giriş sorusunun cevabı. Diske YAZILMIYOR — parola niteliğinde ve her
   * açılışta zaten yeniden soruluyor. Online masaya bağlanırken sunucuya
   * gidiyor.
   */
  const [pass, setPass] = useState('');
  const [tables, setTables] = useState<Partial<Record<string, TableStatus>>>({});

  const enter = (value: string, answer: string) => {
    setName(value);
    setPass(answer);
    try {
      localStorage.setItem(NAME_KEY, value);
    } catch {
      /* depolama yoksa ad yalnızca bu oturumda geçerli */
    }
  };

  /** Salon listesi masaya oturmadan da canlı kalıyor. Sunucu yoksa hiç açılmaz. */
  useEffect(
    () =>
      watchLobby((raw) => {
        const next: Record<string, TableStatus> = {};
        for (const [code, info] of Object.entries(raw)) {
          const row = info as { players?: string[]; status?: string };
          next[code] = {
            players: row.players ?? [],
            playing: row.status === 'playing',
          };
        }
        setTables(next);
      }),
    [],
  );

  /**
   * Yarım kalmış bot maçı var mı. Salona her dönüşte yeniden bakılıyor:
   * maç bitince kayıt siliniyor, "devam et" de onunla birlikte kayboluyor.
   */
  const [canResume, setCanResume] = useState(() => loadLocal() !== null);
  useEffect(() => {
    if (game.status === 'idle') setCanResume(loadLocal() !== null);
  }, [game.status]);

  const view = game.view;
  const atTable = view !== null && (game.status === 'playing' || game.status === 'over');
  const inRoom = game.status !== 'idle' && game.status !== 'error';

  /**
   * Yeni sürüm hazırsa uygula — ama YALNIZCA masadan uzaktayken. Uygulamak
   * sayfayı yeniden yüklüyor; maçın ortasında yapılsaydı oyun kaybolurdu.
   */
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => onUpdateReady(() => setUpdateReady(true)), []);
  useEffect(() => {
    if (updateReady && !inRoom) applyUpdate();
  }, [updateReady, inRoom]);

  if (!name) return <Login initialName={readName()} onEnter={enter} />;

  return (
    <div className="app">
      {/* Yatay uyarısı YALNIZCA masada: giriş ve salon dikeyde de sorunsuz. */}
      {atTable && <RotateHint />}

      {view && atTable ? (
        <Table
          view={view}
          display={game.display}
          seats={game.seats}
          busy={game.busy}
          sent={game.sent}
          startsAt={game.startsAt}
          live={game.live ?? view}
          canStart={game.canStart}
          onStart={game.startMatch}
          autoPlay={game.autoPlay}
          onToggleAutoPlay={game.toggleAutoPlay}
          chat={game.chat}
          phrases={game.phrases}
          onPlay={game.play}
          onTrump={game.chooseTrump}
          onBury={game.bury}
          onExchange={game.exchange}
          onChat={game.sendChat}
          onQuit={game.quit}
        />
      ) : inRoom ? (
        <div className="gate">
          <Logo />
          <p>{game.tablePlayers.join(' · ') || 'Masa kuruluyor…'}</p>
          {game.startsAt ? (
            <Countdown at={game.startsAt} />
          ) : (
            <p className="gate__sub">Oyuncu bekleniyor</p>
          )}
          <button className="btn btn--pill" onClick={game.quit} type="button">
            Vazgeç
          </button>
        </div>
      ) : (
        <Lobby
          name={name}
          tables={tables}
          canResume={canResume}
          onSit={(code) => void game.connect(name, code, pass)}
          onBots={() => game.startLocal(name)}
          onResume={() => game.startLocal(name, true)}
        />
      )}

      {game.error && <div className="toast">{game.error}</div>}

      {game.status === 'over' && view && (
        <div className="scrim">
          <section className="dialog">
            <header className="dialog__head">
              <h3>MAÇ BİTTİ</h3>
              <span className="dialog__sub">12 el · 3 set</span>
            </header>

            <table className="board-table">
              <tbody>
                {[...game.seats]
                  .sort((a, b) => (view.scores[b.seat] ?? 0) - (view.scores[a.seat] ?? 0))
                  .map((s, index) => (
                    <tr key={s.seat} className={s.seat === view.you ? 'is-me' : undefined}>
                      <td className={`place place--${index + 1}`}>{index + 1}</td>
                      <td className="board-table__name">{s.name}</td>
                      <td className="board-table__score">
                        {(view.scores[s.seat] ?? 0) > 0 ? '+' : ''}
                        {view.scores[s.seat] ?? 0}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="actions">
              <button className="btn btn--primary btn--pill" onClick={() => game.startLocal(name)}>
                Yeni oyun
              </button>
              <button className="btn btn--pill" onClick={game.quit}>
                Salona dön
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
